import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';
import { computeJurorScores } from '../../db/selectors/scoreComputation';

/** One height for every filter control so the bars read as a single unit. */
export const FILTER_CONTROL_HEIGHT = 32;
const FILTER_FONT_SIZE = 14;
// Pill-shaped controls on a white fill, matching the chip silhouette,
// so search, selects, and chips read as one family.
const FILTER_RADIUS = FILTER_CONTROL_HEIGHT / 2;
const FILTER_BG = 'white';

/**
 * Shared filter helpers and controls for the jury box, dismissed area,
 * and jury pool. All matching is done client-side over already-decrypted
 * juror objects, so filters apply instantly with no Dexie round-trips.
 */

/** Case-insensitive text match over name, comments, and demographic values. */
export function jurorMatchesText(juror, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  if (juror.nameAndId?.toLowerCase().includes(t)) return true;
  if (juror.comments?.toLowerCase().includes(t)) return true;
  return (juror.demographics || []).some(
    (d) => d?.value?.toLowerCase?.().includes(t),
  );
}

/**
 * Faceted demographic match: OR within a category, AND across categories.
 * @param {Object} juror
 * @param {Array<{name: string, value: string}>} selections
 */
export function jurorMatchesDemographics(juror, selections) {
  if (!selections || selections.length === 0) return true;

  const byCategory = new Map();
  for (const sel of selections) {
    if (!byCategory.has(sel.name)) byCategory.set(sel.name, []);
    byCategory.get(sel.name).push(sel.value);
  }

  for (const [name, values] of byCategory) {
    const jurorValues = (juror.demographics || [])
      .filter((d) => d?.name === name)
      .map((d) => d.value);
    if (!values.some((v) => jurorValues.includes(v))) {
      return false;
    }
  }
  return true;
}

/**
 * OR across the selected reason chips. 'both' is its own reason, distinct
 * from the single-side strikes (matching how it is scored and displayed).
 */
export function jurorMatchesReasons(juror, reasonKeys) {
  if (!reasonKeys || reasonKeys.length === 0) return true;
  return reasonKeys.some((key) => {
    if (key === 'cause' || key === 'hardship') {
      return juror.discardedReason === key;
    }
    if (key === 'prosecution') {
      return juror.discardedReason === 'peremptory' && juror.discarder === 'prosecutor';
    }
    if (key === 'defense') {
      return juror.discardedReason === 'peremptory' && juror.discarder === 'defense';
    }
    if (key === 'both') {
      return juror.discardedReason === 'peremptory' && juror.discarder === 'both';
    }
    return false;
  });
}

export const JUROR_STATUSES = [
  { key: 'keep', label: 'Keep', tooltip: 'Intend to keep' },
  { key: 'question', label: 'Question', tooltip: 'Question further' },
  { key: 'dismiss', label: 'Dismiss', tooltip: 'Intend to dismiss' },
];

/** OR across the selected intent statuses. */
export function jurorMatchesStatuses(juror, statusKeys) {
  if (!statusKeys || statusKeys.length === 0) return true;
  return statusKeys.includes(juror.status);
}

export const SCORE_FILTER_OPERATORS = [
  { key: 'gte', label: 'at least (≥)' },
  { key: 'lte', label: 'at most (≤)' },
  { key: 'between', label: 'between' },
];

/**
 * A juror matches a score filter when: (a) its completeness for that score
 * (answeredCount / ruleCategoryCount) meets the chosen acceptable ratio, and
 * (b) its total satisfies the chosen operator/value(s). A score with no
 * rule-bearing categories (ruleCategoryCount === 0) is always treated as
 * 100% complete — there's nothing to be incomplete about.
 * @param {Object} caseObj - Case record with scoreDefinitions/scoringRules.
 * @param {Object} juror
 * @param {{scoreDefinitionId: string, minCompleteness?: number, operator: 'gte'|'lte'|'between', value: number, valueTo?: number}|null} filter
 */
export function jurorMatchesScoreFilter(caseObj, juror, filter) {
  if (!filter || !filter.scoreDefinitionId) return true;

  const scores = computeJurorScores(caseObj, juror);
  const score = scores.find((s) => s.scoreDefinitionId === filter.scoreDefinitionId);
  if (!score) return true;

  const completeness = score.ruleCategoryCount === 0 ? 1 : (score.answeredCount / score.ruleCategoryCount);
  if (completeness < (filter.minCompleteness ?? 0)) return false;

  const value = Number(filter.value) || 0;
  if (filter.operator === 'lte') return score.total <= value;
  if (filter.operator === 'between') {
    const valueTo = Number(filter.valueTo) || 0;
    const [lo, hi] = value <= valueTo ? [value, valueTo] : [valueTo, value];
    return score.total >= lo && score.total <= hi;
  }
  // Default / 'gte'
  return score.total >= value;
}

/**
 * Shared filter state for the common filters (text search + demographics)
 * across the jury box, dismissed area, and jury pool. Searching in one area
 * searches them all; area-specific filters (intent, dismissal reason) stay
 * local to their components. Living above the areas also means the dismissed
 * filters survive the DismissedBox -> DismissQuadrant swap during drags.
 */
const SharedJurorFiltersContext = React.createContext(null);

export function SharedJurorFiltersProvider({ children }) {
  const [search, setSearch] = React.useState('');
  const [demographics, setDemographics] = React.useState([]);
  const [scoreFilter, setScoreFilter] = React.useState(null);

  const value = React.useMemo(
    () => ({ search, setSearch, demographics, setDemographics, scoreFilter, setScoreFilter }),
    [search, demographics, scoreFilter],
  );

  return (
    <SharedJurorFiltersContext.Provider value={value}>
      {children}
    </SharedJurorFiltersContext.Provider>
  );
}

/** Falls back to component-local state when no provider is mounted. */
export function useSharedJurorFilters() {
  const ctx = React.useContext(SharedJurorFiltersContext);
  const [localSearch, setLocalSearch] = React.useState('');
  const [localDemographics, setLocalDemographics] = React.useState([]);
  const [localScoreFilter, setLocalScoreFilter] = React.useState(null);

  return (
    ctx ?? {
      search: localSearch,
      setSearch: setLocalSearch,
      demographics: localDemographics,
      setDemographics: setLocalDemographics,
      scoreFilter: localScoreFilter,
      setScoreFilter: setLocalScoreFilter,
    }
  );
}

/** Debounce fast-changing values (e.g. search keystrokes) before filtering. */
export function useDebouncedValue(value, delayMs = 150) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Single-line container for an area's filter controls. Wraps only when
 * the viewport genuinely can't fit them.
 */
export function FilterBar({ children, style }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/**
 * The standard text search field used by every filter bar — same height,
 * font, icon, and clear affordance everywhere.
 */
export function FilterSearchField({ value, onChange, placeholder = 'Search', width = 200 }) {
  return (
    <TextField
      size="small"
      variant="outlined"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width }}
      InputProps={{
        style: {
          height: FILTER_CONTROL_HEIGHT,
          fontSize: FILTER_FONT_SIZE,
          backgroundColor: FILTER_BG,
          borderRadius: FILTER_RADIUS,
        },
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" style={{ color: 'rgba(0,0,0,0.35)' }} />
          </InputAdornment>
        ),
        endAdornment: value.length > 0 ? (
          <InputAdornment position="end">
            <IconButton size="small" style={{ padding: 2 }} onClick={() => onChange('')}>
              <Close fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );
}

/**
 * Generic row of toggleable filter chips.
 * @param {{
 *   options: Array<{key: string, label: string, tooltip?: string}>,
 *   value: string[],
 *   onChange: (keys: string[]) => void,
 * }} props
 */
export function FilterChips({ options, value, onChange }) {
  const toggle = (key) => {
    onChange(
      value.includes(key)
        ? value.filter((k) => k !== key)
        : [...value, key],
    );
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {options.map(({ key, label, tooltip }) => {
        const active = value.includes(key);
        const chip = (
          <Chip
            key={key}
            label={label}
            clickable
            color={active ? 'primary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => toggle(key)}
            style={{
              height: FILTER_CONTROL_HEIGHT,
              fontSize: FILTER_FONT_SIZE,
              backgroundColor: active ? undefined : FILTER_BG,
            }}
          />
        );
        return tooltip ? (
          <Tooltip key={key} title={tooltip}>
            {chip}
          </Tooltip>
        ) : (
          chip
        );
      })}
    </div>
  );
}

/**
 * Dismissal reason chips. Strike labels follow the case type
 * (Plaintiff for civil, Prosecution for criminal), and the separate
 * "Both" reason only appears when it is actually in use.
 *
 * @param {{
 *   value: string[],
 *   onChange: Function,
 *   civilCase?: boolean,
 *   showBoth?: boolean,
 * }} props
 */
export function DismissalReasonChips({ value, onChange, civilCase = false, showBoth = false }) {
  const strikeSide = civilCase ? 'Plaintiff' : 'Pros.';
  const options = [
    { key: 'cause', label: 'Cause' },
    { key: 'hardship', label: 'Hardship' },
    { key: 'prosecution', label: `${strikeSide} strike` },
    { key: 'defense', label: 'Def. strike' },
    ...(showBoth ? [{ key: 'both', label: 'Both strike' }] : []),
  ];

  return <FilterChips options={options} value={value} onChange={onChange} />;
}

/** Intent (juror status) chips: Keep / Question / Dismiss. */
export function StatusFilterChips({ value, onChange }) {
  return <FilterChips options={JUROR_STATUSES} value={value} onChange={onChange} />;
}

/**
 * Compact multi-select of demographic values, grouped by category.
 * Options are derived from the jurors actually present in the area being
 * filtered, so there are never dead options. Renders nothing when the
 * jurors carry no demographics.
 *
 * @param {{
 *   jurors: Array,
 *   value: Array<{name: string, value: string}>,
 *   onChange: Function,
 *   placeholder?: string,
 *   minWidth?: number,
 * }} props
 */
export function DemographicsFilterSelect({
  jurors,
  value,
  onChange,
  placeholder = 'Filter by demographic',
  minWidth = 240,
  maxWidth = 340,
}) {
  const options = React.useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const juror of jurors) {
      for (const d of juror.demographics || []) {
        if (!d?.name || !d?.value) continue;
        const key = `${d.name} ${d.value}`;
        if (seen.has(key)) continue;
        seen.add(key);
        opts.push({ name: d.name, value: d.value, key });
      }
    }
    // Selections are shared across areas — include ones this area's jurors
    // don't carry so MUI doesn't warn about values missing from options.
    for (const sel of value) {
      if (!seen.has(sel.key)) {
        seen.add(sel.key);
        opts.push(sel);
      }
    }
    opts.sort(
      (a, b) => a.name.localeCompare(b.name) || a.value.localeCompare(b.value),
    );
    return opts;
  }, [jurors, value]);

  if (options.length === 0 && value.length === 0) {
    return null;
  }

  return (
    <Autocomplete
      multiple
      size="small"
      disableCloseOnSelect
      options={options}
      groupBy={(o) => o.name}
      getOptionLabel={(o) => o.value}
      isOptionEqualToValue={(o, v) => o.key === v.key}
      value={value}
      onChange={(e, next) => onChange(next)}
      limitTags={2}
      style={{ minWidth, maxWidth }}
      sx={{
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          minHeight: FILTER_CONTROL_HEIGHT,
          paddingTop: '1px',
          paddingBottom: '1px',
          fontSize: FILTER_FONT_SIZE,
          backgroundColor: FILTER_BG,
          borderRadius: `${FILTER_RADIUS}px`,
        },
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((opt, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              {...tagProps}
              key={opt.key}
              size="small"
              label={`${opt.name}: ${opt.value}`}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder={value.length === 0 ? placeholder : ''}
          inputProps={{
            ...params.inputProps,
            style: { ...params.inputProps?.style, fontSize: FILTER_FONT_SIZE },
          }}
        />
      )}
    />
  );
}

const filterSelectSx = {
  height: FILTER_CONTROL_HEIGHT,
  fontSize: FILTER_FONT_SIZE,
  backgroundColor: FILTER_BG,
  borderRadius: `${FILTER_RADIUS}px`,
  '& .MuiSelect-select': { display: 'flex', alignItems: 'center' },
};

/**
 * Filter by a single computed score: pick the score, an operator
 * (gte/lte/between), the value(s), and how complete a juror's answers for
 * that score must be to count at all. Renders nothing when the case has no
 * scores defined.
 *
 * @param {{
 *   scoreDefinitions: Array<{_id: string, name: string}>,
 *   value: {scoreDefinitionId: string, operator: string, value: number, valueTo?: number, minCompleteness?: number}|null,
 *   onChange: (next: Object|null) => void,
 * }} props
 */
export function ScoreFilterControl({ scoreDefinitions, value, onChange }) {
  if (!scoreDefinitions || scoreDefinitions.length === 0) {
    return null;
  }

  const filter = value || {};
  const active = !!filter.scoreDefinitionId;
  const update = (updates) => onChange({ ...filter, ...updates });

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField
        select
        size="small"
        variant="outlined"
        value={filter.scoreDefinitionId || ''}
        onChange={(e) => {
          if (!e.target.value) {
            onChange(null);
            return;
          }
          update({
            scoreDefinitionId: e.target.value,
            operator: filter.operator || 'gte',
            value: filter.value ?? 0,
            minCompleteness: filter.minCompleteness ?? 0,
          });
        }}
        displayEmpty
        style={{ minWidth: 150 }}
        SelectProps={{ sx: filterSelectSx }}
      >
        <MenuItem value=""><em>Filter by score…</em></MenuItem>
        {scoreDefinitions.map((score) => (
          <MenuItem key={score._id} value={score._id}>{score.name}</MenuItem>
        ))}
      </TextField>

      {active && (
        <React.Fragment>
          <TextField
            select
            size="small"
            variant="outlined"
            value={filter.operator || 'gte'}
            onChange={(e) => update({ operator: e.target.value })}
            style={{ minWidth: 120 }}
            SelectProps={{ sx: filterSelectSx }}
          >
            {SCORE_FILTER_OPERATORS.map((op) => (
              <MenuItem key={op.key} value={op.key}>{op.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            value={filter.value ?? 0}
            onChange={(e) => update({ value: parseFloat(e.target.value) || 0 })}
            style={{ width: 70 }}
            InputProps={{ sx: filterSelectSx }}
          />
          {filter.operator === 'between' && (
            <React.Fragment>
              <Typography variant="body2">and</Typography>
              <TextField
                type="number"
                size="small"
                variant="outlined"
                value={filter.valueTo ?? 0}
                onChange={(e) => update({ valueTo: parseFloat(e.target.value) || 0 })}
                style={{ width: 70 }}
                InputProps={{ sx: filterSelectSx }}
              />
            </React.Fragment>
          )}
          <TextField
            select
            size="small"
            variant="outlined"
            value={filter.minCompleteness ?? 0}
            onChange={(e) => update({ minCompleteness: parseFloat(e.target.value) })}
            style={{ minWidth: 150 }}
            SelectProps={{ sx: filterSelectSx }}
          >
            <MenuItem value={0}>Any completeness</MenuItem>
            <MenuItem value={0.5}>≥ half answered</MenuItem>
            <MenuItem value={1}>Fully answered</MenuItem>
          </TextField>
          <IconButton size="small" onClick={() => onChange(null)}>
            <Close fontSize="small" />
          </IconButton>
        </React.Fragment>
      )}
    </Stack>
  );
}
